import { useState } from 'react';
import { Box, styled } from '@mui/material';
import { TFormType } from 'src/@types';
import { DeviceToggle } from 'src/components/device-toggle/DeviceToggle';
import { TDeviceType } from 'src/static/constants';
import { toFileItem } from 'src/utils/fileUploadUtils';

import { GuideArea } from '../guide/ServiceGuide';
import MediaRenderer from '../hook-form/uploadImage/sections/MediaRenderer';

interface PreviewProps {
  formType?: TFormType;
  areas: GuideArea[];
  values?: Record<string, any>;
}

const Preview = ({ values }: PreviewProps) => {
  const [device, setDevice] = useState<TDeviceType>('desktop');

  return (
    <PreviewContainer>
      <DeviceToggle
        device={device}
        onDeviceChange={(_, newDevice) => newDevice && setDevice(newDevice)}
      />
      <PreviewFrame device={device}>
        {values?.bgPath?.[0] && (
          <MediaWrapper>
            <MediaRenderer file={toFileItem(values.bgPath[0], 0)} />
          </MediaWrapper>
        )}
        <CenterContent>
          <MainTitle
            device={device}
            style={{ color: values?.mainTextColor }}
            dangerouslySetInnerHTML={{
              __html: values?.mainText?.replace(/\n/g, '<br/>') || 'Default Title',
            }}
          />
          <Subtitle
            device={device}
            style={{ color: values?.subTextColor }}
            dangerouslySetInnerHTML={{
              __html: values?.subText?.replace(/\n/g, '<br/>') || 'Default Subtitle',
            }}
          />
        </CenterContent>
      </PreviewFrame>
    </PreviewContainer>
  );
};

export default Preview;

const MediaWrapper = styled(Box)({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '100%',
  height: '100%',
  '& img, & video': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
  },
});

const CenterContent = styled(Box)({
  position: 'relative',
  zIndex: 1,
  padding: '20px',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  justifyContent: 'center',
  width: '100%',
  height: '100%',
});

const PreviewContainer = styled(Box)({
  width: '100%',
  overflow: 'hidden',
  position: 'relative',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const PreviewFrame = styled(Box)<{ device: TDeviceType }>(({ device }) => ({
  position: 'relative',
  width: device === 'mobile' ? '280px' : device === 'tablet' ? '600px' : '800px',
  height: '500px',
  overflow: 'hidden',
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  margin: '0 auto',
  transform: 'scale(0.9)',
}));

const MainTitle = styled('div')<{ device: string }>(({ device }) => ({
  textAlign: 'center',
  fontSize: device === 'mobile' ? '20px' : device === 'tablet' ? '35px' : '45px',
  lineHeight: '1.5',
  letterSpacing: '-2%',
  fontWeight: 'bold',
}));

const Subtitle = styled('p')<{ device: string }>(({ device }) => ({
  textAlign: 'center',
  marginTop: device === 'mobile' ? '20px' : device === 'tablet' ? '28px' : '30px',
  fontSize: device === 'mobile' ? '14px' : device === 'tablet' ? '16px' : '17px',
  lineHeight: device === 'mobile' ? '20px' : '1.5',
  letterSpacing: '-2%',
}));
